import * as Sentry from '@sentry/sveltekit';

/**
 * Custom Sentry utilities for tracking swap-related issues
 */

export interface NoRoutesFoundContext {
    sellToken: string;
    buyToken: string;
    sellAmount: number;
    isRaceCondition: boolean;
    hasInstructions: boolean;
    hasResults: boolean;
    loadingState: {
        isLoadingQuotes: boolean;
        isFetchingQuotes: boolean;
        isSimulating: boolean;
    };
}

/**
 * Creates a structured event for "No Routes Found" issues
 */
export function captureNoRoutesFoundEvent(context: NoRoutesFoundContext) {
    return Sentry.captureEvent({
        message: 'No Routes Found Analysis',
        level: 'warning',
        tags: {
            issue_category: 'swap_flow',
            issue_type: 'no_routes_found',
            potential_race_condition: context.isRaceCondition.toString(),
            has_instructions: context.hasInstructions.toString(),
            has_results: context.hasResults.toString(),
        },
        extra: {
            swap_pair: `${context.sellToken}->${context.buyToken}`,
            sell_amount: context.sellAmount,
            race_condition_analysis: {
                is_likely_race_condition: context.isRaceCondition,
                has_instructions_but_no_results: context.hasInstructions && !context.hasResults,
                loading_states: context.loadingState
            },
            debugging_context: context
        },
        fingerprint: [
            'no-routes-found',
            context.sellToken,
            context.buyToken,
            context.isRaceCondition ? 'race-condition' : 'legitimate'
        ]
    });
}

/**
 * Set up custom filters for the No Routes Found issue
 */
export function setupNoRoutesFoundMonitoring() {
    // Custom before-send filter to enhance No Routes Found events
    Sentry.getCurrentScope().addEventProcessor((event) => {
        // Only process our specific "No Routes Found" events
        if (event.message === 'No Routes Found displayed to user') {
            // Add additional context for better debugging
            if (event.extra) {
                const isRaceCondition = 
                    event.extra.hasSimulationInstructions && 
                    !event.extra.hasSimulationResults &&
                    (event.extra.isLoadingQuotes || event.extra.isFetchingQuotes || event.extra.isSimulating);

                event.extra.race_condition_analysis = {
                    is_likely_race_condition: isRaceCondition,
                    fix_effectiveness: isRaceCondition ? 'FIX_NEEDED' : 'WORKING_AS_EXPECTED'
                };

                // Update fingerprint for better grouping
                event.fingerprint = [
                    'submit-button-no-routes',
                    String(event.extra.sellToken || 'unknown'),
                    String(event.extra.buyToken || 'unknown'),
                    isRaceCondition ? 'race-condition' : 'legitimate'
                ];
            }
        }

        return event;
    });
}

/**
 * Alert conditions you can set up in Sentry
 */
export const SENTRY_ALERT_CONDITIONS = {
    // Alert if we see more than 5 "No Routes Found" events in 5 minutes
    HIGH_VOLUME_NO_ROUTES: {
        condition: 'event.count',
        value: 5,
        timeWindow: '5m',
        filter: 'message:"No Routes Found displayed to user"'
    },
    
    // Alert specifically for race condition cases (indicates fix isn't working)
    RACE_CONDITION_DETECTED: {
        condition: 'event.count',
        value: 1,
        timeWindow: '1m',
        filter: 'message:"No Routes Found displayed to user" extra.race_condition_analysis.is_likely_race_condition:true'
    },
    
    // Alert for high error rate in route checking
    ROUTE_AVAILABILITY_ERRORS: {
        condition: 'event.count',
        value: 10,
        timeWindow: '5m',
        filter: 'breadcrumbs.category:"route_availability" breadcrumbs.level:"warning"'
    }
}; 